import React from 'react';
import { Wrench } from 'lucide-react';
import { PersistedWorkflow } from '@cotera/sdk/core';
import { coco } from '@cotera/api';
import { Text } from '@cotera/client/app/components/ui';
import { PROVIDER_ICONS } from '@cotera/client/v0/components/tool-connections/constants';
import { getProviderFromToolKey } from '@cotera/client/v0/components/tool-connections/utils';
import type {
  WorkflowNodeRenderStrategy,
  WorkflowNodeViewData,
} from '../types';
import { useDatasetOptions } from '@cotera/client/app/v1/trigger/hooks';
import { asRecord, readString } from '../../utils';
import { readParamBindings } from '../edit-strategies/json-pointer-suggestions';

const { WorkflowNodeType } = PersistedWorkflow;

function toolKeyFromNode(node: WorkflowNodeViewData): string | undefined {
  return readString(asRecord(node.data), 'key');
}

function toolNameFromNode(node: WorkflowNodeViewData): string | undefined {
  return readString(asRecord(node.data), 'name');
}

/** "google-sheets" -> "Google Sheets" */
function humanize(segment: string): string {
  return segment
    .split(/[-_]/)
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** "slack/messaging/send-message" -> "Send message" (the action segment). */
function actionFromKey(key: string): string | undefined {
  const lastSegment = key
    .split('/')
    .filter((segment) => segment.length > 0)
    .pop();
  return lastSegment !== undefined ? humanize(lastSegment) : undefined;
}

/**
 * One-line preview of the first couple of configured params, so the card shows
 * *what* the tool will do (e.g. `channel: #growth · text: Daily digest`) instead
 * of generic boilerplate. Only primitive values are shown; nested objects are
 * skipped since they don't render meaningfully on one line.
 */
function paramPreview(
  node: WorkflowNodeViewData,
  /** Turns a resource id into the name a person would recognize it by. */
  resolveName?: (paramKey: string, id: string) => string | undefined
): string | undefined {
  const params = asRecord(node.data)?.['params'];
  const record = asRecord(params);
  if (record === null) {
    return undefined;
  }
  const parts: string[] = [];
  for (const [paramKey, value] of Object.entries(record)) {
    if (parts.length >= 2) {
      break;
    }
    if (typeof value === 'string' && value.trim() !== '') {
      // A resolved name stands alone: "YC Batch" says more on a card than
      // "datasetId: 18aa781e-…", and the key was only ever there to make an
      // opaque value legible.
      const named = resolveName?.(paramKey, value);
      parts.push(named !== undefined ? named : `${paramKey}: ${value}`);
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      parts.push(`${paramKey}: ${String(value)}`);
    }
  }
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/** Tools whose card should name the dataset rather than show its uuid. */
const DATASET_TOOL_KEYS: ReadonlySet<string> = new Set([
  coco.COCO_DATASET_READ_TOOL_KEY,
  coco.COCO_DATASET_WRITE_TOOL_KEY,
  coco.COCO_DATASET_EDIT_TOOL_KEY,
]);

const ToolCallBodyText: React.FC<{ label: string }> = ({ label }) => (
  <Text.Caption className="text-muted-foreground block truncate">
    {label}
  </Text.Caption>
);

/**
 * A dataset tool's card, showing the dataset by name.
 *
 * Its own component because resolving the name needs a hook, and `renderBody`
 * is a plain function called from whichever card is drawing — a hook there
 * would run in the parent and change order with the node type.
 */
const DatasetToolCallBody: React.FC<{ node: WorkflowNodeViewData }> = ({
  node,
}) => {
  const datasets = useDatasetOptions();
  const label =
    paramPreview(node, (paramKey, id) =>
      paramKey === 'datasetId'
        ? datasets.find((dataset) => dataset.id === id)?.name
        : undefined
    ) ??
    bindingPreview(node) ??
    'No inputs configured';
  return <ToolCallBodyText label={label} />;
};

function bindingPreview(node: WorkflowNodeViewData): string | undefined {
  const bindings = readParamBindings(asRecord(node.data));
  const entries = Object.entries(bindings);
  if (entries.length === 0) {
    return undefined;
  }
  const parts = entries.slice(0, 2).map(([paramKey, pointer]) => {
    const shortPointer =
      pointer.length > 28 ? `${pointer.slice(0, 25)}…` : pointer;
    return `${paramKey} ← ${shortPointer}`;
  });
  return parts.join(' · ');
}

export const toolCallNodeStrategy: WorkflowNodeRenderStrategy = {
  nodeType: WorkflowNodeType.ToolCall,
  accent: 'tool',
  badge: 'Tool',
  getTitle: (node) => {
    const name = toolNameFromNode(node);
    if (name !== undefined && name !== '') {
      return name;
    }
    const key = toolKeyFromNode(node);
    if (key === undefined) {
      return 'Tool call';
    }
    const lastSegment = key
      .split('/')
      .filter((segment) => segment.length > 0)
      .pop();
    return lastSegment !== undefined ? humanize(lastSegment) : 'Tool call';
  },
  getDescription: (node) => {
    const key = toolKeyFromNode(node);
    if (key === undefined) {
      return undefined;
    }
    const provider = humanize(getProviderFromToolKey(key));
    const name = toolNameFromNode(node);
    const action = actionFromKey(key);
    // With a custom name the title hides the action, so surface it here as
    // `Provider · Action`. Without a name the title is already the action.
    if (name !== undefined && name !== '' && action !== undefined) {
      return `${provider} · ${action}`;
    }
    return provider;
  },
  renderIcon: (node) => {
    const key = toolKeyFromNode(node);
    const provider =
      key !== undefined ? getProviderFromToolKey(key) : undefined;
    const ProviderIcon =
      provider !== undefined ? PROVIDER_ICONS[provider] : undefined;
    if (ProviderIcon !== undefined) {
      return (
        <span className="flex h-4 w-4 items-center justify-center [&_svg]:h-4 [&_svg]:w-4">
          <ProviderIcon />
        </span>
      );
    }
    return <Wrench className="h-4 w-4" />;
  },
  renderBody: (node) => {
    const key = toolKeyFromNode(node);
    if (key === undefined) {
      return (
        <Text.Caption className="text-muted-foreground block">
          Tool not set.
        </Text.Caption>
      );
    }
    if (DATASET_TOOL_KEYS.has(key)) {
      return <DatasetToolCallBody node={node} />;
    }
    const preview = paramPreview(node);
    const bindings = bindingPreview(node);
    const label =
      bindings !== undefined
        ? bindings
        : preview !== undefined
        ? preview
        : 'No inputs configured';
    return <ToolCallBodyText label={label} />;
  },
};
